import React, { useState, useEffect, useContext } from "react";
import {
  Grid,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Button,
  Autocomplete,
  Typography,
  Box,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { UserContext } from "context/UserContext";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axiosInstance from "services/axiosConfig";

const steps = ["Müşteri Seçimi", "Talep Detayları", "Miktar & Tarih"];

// Styling for all TextFields
const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    height: "56px",
    display: "flex",
    alignItems: "center",
  },
  "& .MuiOutlinedInput-input": {
    padding: "12px 14px",
  },
};

const materialTypes = ["Kaya Tuzu", "Gol Tuzu", "Deniz Tuzu"];
const saltTypes = [
  "0-1 mm",
  "1-2.2 mm",
  "2.2-4 mm",
  "4+ mm",
  "Hayvan Yalama",
  "Göl No. 1",
  "Göl No. 2",
  "Göl No. 3",
  "Tablet",
  "Piramit",
  "Dip Tuzu",
];
const classifications = ["Ham", "Optik Seçilmiş", "Yıkanmış", "Kurutulmuş", "Sera"];
const packagings = [
  "Dökme",
  "25 kg Çuval",
  "50 kg Çuval",
  "Bigbag İçi Dökme",
  "Bigbag 25 kg",
  "Bigbag 50 kg",
  "Palet 25 kg",
  "Palet 50 kg",
];
const paymentTypes = ["Peşin", "Taksit"];

function CreateOrder() {
  const { user, loading: userLoading } = useContext(UserContext);
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [factories, setFactories] = useState([]);
  const [selectedFactory, setSelectedFactory] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    customerId: "",
    customerName: "",
    materialType: "",
    saltSize: "",
    classification: "",
    packaging: "",
    paymentType: "",
    term: "",
    amount: "",
    quantityType: "",
    totalTons: "",
    price: "",
    pricePerTon: "",
    deliveryDate: "",
    selectedFactoryId: "",
    fabrikaAdi: "",
    fabrikaSorumlusu: "",
    fabrikaTelefon: "",
    fabrikaAdres: "",
    comment: "",
  });

  const parseAmountInput = (value) => {
    if (!value) return null;
    const normalized = value.replace(/\./g, "");
    return normalized ? Number(normalized) : null;
  };

  const formatAmountInput = (value) => {
    const digits = value.replace(/[^\d]/g, "");
    if (!digits) return "";
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  };

  const calculateTotalKilograms = (amountValue, quantityTypeValue) => {
    const numericAmount = parseAmountInput(amountValue);
    if (!amountValue || numericAmount === null || Number.isNaN(numericAmount)) return "";
    if (quantityTypeValue === "Tır") return numericAmount * 27 * 1000;
    if (quantityTypeValue === "Ton") return numericAmount * 1000;
    if (quantityTypeValue === "Kilogram") return numericAmount;
    return "";
  };

  const calculateTotalTons = (amountValue, quantityTypeValue) => {
    const kilograms = calculateTotalKilograms(amountValue, quantityTypeValue);
    if (kilograms === "") return "";
    return kilograms / 1000;
  };

  const formatKilogramValue = (kilograms) => {
    if (kilograms === "" || kilograms === null || kilograms === undefined) {
      return "";
    }
    const numeric = Number(kilograms);
    if (!Number.isFinite(numeric)) return "";
    const rounded = Math.round(numeric * 100) / 100;
    const displayValue = Number.isInteger(rounded) ? rounded.toString() : rounded.toFixed(2);
    return `${displayValue} kg`;
  };

  const formatPriceInput = (value) => {
    const digits = value.replace(/[^\d]/g, "");
    if (!digits) return "";
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  };

  const parsePriceInput = (value) => {
    if (!value) return null;
    const normalized = value.replace(/\./g, "");
    return normalized ? Number(normalized) : null;
  };

  // Check access rights
  const hasAccess =
    user &&
    user.roles &&
    (user.roles.includes("ROLE_MANAGER") ||
      user.roles.includes("ROLE_SALES_RESPONSIBLE") ||
      user.roles.includes("ROLE_CUSTOMER"));

  const canSelectCustomer =
    user &&
    user.roles &&
    (user.roles.includes("ROLE_MANAGER") || user.roles.includes("ROLE_SALES_RESPONSIBLE"));

  useEffect(() => {
    if (user && !canSelectCustomer) {
      // Customer user can only select themselves
      fetchCurrentCustomer();
    }
  }, [user, canSelectCustomer]);

  useEffect(() => {
    if (canSelectCustomer) {
      fetchCustomers();
    }
  }, [canSelectCustomer]);

  const fetchCurrentCustomer = async () => {
    setLoadingCustomers(true);
    try {
      const response = await axiosInstance.get("sales/customer/me");
      const customer = response.data;
      if (customer) {
        setFormData((prev) => ({
          ...prev,
          customerId: customer.id,
          customerName: customer.name,
          customerTradeName: customer.ticariUnvan || "",
          customerEmail: customer.eposta || "",
          customerPhone: customer.telefon || "",
        }));
        // Load factories for the current customer (pass the customer object directly)
        loadFactoriesForCustomer(customer);
      }
    } catch (error) {
      console.error("Error fetching current customer:", error);
      toast.error("Müşteri bilgisi alınamadı");
    } finally {
      setLoadingCustomers(false);
    }
  };

  const fetchCustomers = async () => {
    setLoadingCustomers(true);
    try {
      const endpoint = user.roles.includes("ROLE_MANAGER")
        ? "manager/getAllCustomers"
        : "sales/getAllCustomers";
      const response = await axiosInstance.get(endpoint);
      const customers = response.data || [];
      setCustomers(customers);
    } catch (error) {
      console.error("Error fetching customers:", error);
      toast.error("Müşteriler yüklenirken bir hata oluştu");
    } finally {
      setLoadingCustomers(false);
    }
  };

  const loadFactoriesForCustomer = (customerIdOrObject) => {
    // Accept either a customer ID or the customer object itself
    let customer;
    if (typeof customerIdOrObject === "object" && customerIdOrObject !== null) {
      customer = customerIdOrObject;
    } else {
      customer = customers.find((c) => c.id === customerIdOrObject);
    }

    if (customer && customer.fabrikalar && Array.isArray(customer.fabrikalar)) {
      setFactories(customer.fabrikalar);

      // Auto-select first factory when available, otherwise clear selection
      if (customer.fabrikalar.length > 0) {
        const factory = customer.fabrikalar[0];
        setSelectedFactory(factory);
        setFormData((prev) => ({
          ...prev,
          selectedFactoryId: factory.id,
          fabrikaAdi: factory.fabrikaAdi || "",
          fabrikaSorumlusu: factory.fabrikaSorumlusu || "",
          fabrikaTelefon: factory.fabrikaTelefon || "",
          fabrikaAdres: factory.adres || "",
        }));
      } else {
        setSelectedFactory(null);
        setFormData((prev) => ({
          ...prev,
          selectedFactoryId: "",
          fabrikaAdi: "",
          fabrikaSorumlusu: "",
          fabrikaTelefon: "",
          fabrikaAdres: "",
        }));
      }
    } else {
      setFactories([]);
      setSelectedFactory(null);
      setFormData((prev) => ({
        ...prev,
        selectedFactoryId: "",
        fabrikaAdi: "",
        fabrikaSorumlusu: "",
        fabrikaTelefon: "",
        fabrikaAdres: "",
      }));
    }
  };

  const handleNext = () => {
    // Validate current step
    if (activeStep === 0 && !formData.customerId) {
      toast.error("Lütfen bir müşteri seçin");
      return;
    }
    if (activeStep === 1) {
      const missingFields = [];
      if (!formData.materialType) missingFields.push("Materyal Tipi");
      if (!formData.saltSize) missingFields.push("Tuz Boyutu");
      if (!formData.paymentType) missingFields.push("Ödeme Tipi");
      if (formData.paymentType === "Taksit" && !formData.term) {
        missingFields.push("Taksit Sayısı");
      }
      if (!formData.classification) missingFields.push("Sınıflandırma");
      if (!formData.packaging) missingFields.push("Paketleme");

      if (missingFields.length > 0) {
        toast.error(`Eksik alanlar: ${missingFields.join(", ")}`);
        return;
      }
    }
    if (activeStep === 2) {
      const missingFields = [];
      if (factories.length === 0) {
        toast.error("Uygun fabrika bulunamadı.");
        return;
      }
      if (!formData.amount) missingFields.push("Miktar");
      if (!formData.quantityType) missingFields.push("Tip");
      if (!formData.deliveryDate) missingFields.push("Teslimat Tarihi");
      if (!formData.fabrikaAdi) missingFields.push("Fabrika");
      if (canSelectCustomer && !parsePriceInput(formData.price)) missingFields.push("Toplam Fiyat");
      if (canSelectCustomer && !parsePriceInput(formData.pricePerTon)) {
        missingFields.push("Ton Başı Fiyat");
      }

      if (missingFields.length > 0) {
        toast.error(`Eksik alanlar: ${missingFields.join(", ")}`);
        return;
      }
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleChange = (field, value) => {
    // Keep term numeric only
    if (field === "term") {
      value = value.replace(/[^0-9]/g, "");
    }
    if (field === "amount") {
      value = formatAmountInput(value);
    }
    if (field === "price" || field === "pricePerTon") {
      value = formatPriceInput(value);
    }
    setFormData((prev) => {
      const next = { ...prev, [field]: value };

      if (field === "amount" || field === "quantityType") {
        next.totalTons = calculateTotalTons(next.amount, next.quantityType);
      }

      return next;
    });
  };

  const handleSubmit = async () => {
    try {
      // Validate all required fields before submission
      const missingFields = [];
      if (factories.length === 0) {
        toast.error("Uygun fabrika bulunamadı.");
        return;
      }
      if (!formData.customerId) missingFields.push("Müşteri");
      if (!formData.materialType) missingFields.push("Materyal Tipi");
      if (!formData.saltSize) missingFields.push("Tuz Boyutu");
      if (!formData.paymentType) missingFields.push("Ödeme Tipi");
      if (formData.paymentType === "Taksit" && !formData.term) {
        missingFields.push("Taksit Sayısı");
      }
      if (!formData.classification) missingFields.push("Sınıflandırma");
      if (!formData.packaging) missingFields.push("Paketleme");
      if (!formData.amount) missingFields.push("Miktar");
      if (!formData.quantityType) missingFields.push("Tip");
      if (!formData.deliveryDate) missingFields.push("Teslimat Tarihi");
      if (!formData.fabrikaAdi) missingFields.push("Fabrika");
      if (canSelectCustomer && !parsePriceInput(formData.price)) missingFields.push("Toplam Fiyat");
      if (canSelectCustomer && !parsePriceInput(formData.pricePerTon)) {
        missingFields.push("Ton Başı Fiyat");
      }

      if (missingFields.length > 0) {
        toast.error(`Eksik alanlar: ${missingFields.join(", ")}`);
        return;
      }

      const amountValue = formData.amount ? formData.amount.toString().trim() : "";
      const totalKilograms = calculateTotalKilograms(amountValue, formData.quantityType);
      const formattedAmount =
        totalKilograms !== ""
          ? formatKilogramValue(totalKilograms)
          : amountValue && formData.quantityType
          ? `${amountValue} ${formData.quantityType}`
          : amountValue;
      const computedTotalTons =
        totalKilograms !== ""
          ? totalKilograms / 1000
          : calculateTotalTons(amountValue, formData.quantityType);

      const requestData = {
        customerId: formData.customerId,
        amount: formattedAmount,
        materialType: formData.materialType,
        paymentType: formData.paymentType,
        term: formData.term ? parseInt(formData.term, 10) : 0,
        size: formData.saltSize,
        classification: formData.classification,
        packaging: formData.packaging,
        requestedDeliveryDate: formData.deliveryDate || new Date().toISOString().split("T")[0],
        comment: formData.comment,
        createdByUsername: user?.username || user?.email || user?.name || "",
        fabrikaInfo: {
          fabrikaAdi: formData.fabrikaAdi,
          fabrikaSorumlusu: formData.fabrikaSorumlusu,
          fabrikaTelefon: formData.fabrikaTelefon,
          adres: formData.fabrikaAdres,
        },
        price: parsePriceInput(formData.price),
        pricePerTon: parsePriceInput(formData.pricePerTon),
        totalTons: computedTotalTons || 0,
      };

      const response = await axiosInstance.post("sales/create-request", requestData);

      if (response.status === 200 || response.status === 201) {
        toast.success("Talep başarıyla oluşturuldu!");
        navigate("/demand");
      }
    } catch (error) {
      console.error("Error creating order:", error);
      toast.error(
        error.response?.data?.message ||
          "Sipariş oluşturulamadı. Lütfen bilgilerinizi kontrol edin."
      );
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <MDBox>
            <MDTypography variant="h6" mb={2}>
              Müşteri Seçimi
            </MDTypography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                {canSelectCustomer ? (
                  <Autocomplete
                    options={customers}
                    getOptionLabel={(option) => option.name || ""}
                    loading={loadingCustomers}
                    value={customers.find((c) => c.id === formData.customerId) || null}
                    onChange={(event, newValue) => {
                      handleChange("customerId", newValue ? newValue.id : "");
                      handleChange("customerName", newValue ? newValue.name : "");
                      if (newValue) {
                        loadFactoriesForCustomer(newValue);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Müşteri *"
                        placeholder="Müşteri seçin"
                        variant="outlined"
                        fullWidth
                      />
                    )}
                  />
                ) : (
                  <TextField
                    label="Müşteri"
                    value={formData.customerName}
                    fullWidth
                    disabled
                    variant="outlined"
                  />
                )}
              </Grid>
              {formData.customerId && (
                <>
                  <Grid item xs={12}>
                    <MDBox
                      p={2}
                      mt={2}
                      borderRadius="lg"
                      sx={{
                        backgroundColor: "grey.100",
                        border: "1px solid",
                        borderColor: "grey.300",
                      }}
                    >
                      <MDTypography variant="h6" mb={2}>
                        Müşteri Bilgileri
                      </MDTypography>
                      <Grid container spacing={2}>
                        {formData.customerId && (
                          <>
                            <Grid item xs={12} md={6}>
                              <Typography variant="body2" color="text.secondary">
                                <strong>Ticari Adı:</strong>{" "}
                                {(canSelectCustomer
                                  ? customers.find((c) => c.id === formData.customerId)?.ticariUnvan
                                  : formData.customerTradeName) || "-"}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Typography variant="body2" color="text.secondary">
                                <strong>E-posta:</strong>{" "}
                                {(canSelectCustomer
                                  ? customers.find((c) => c.id === formData.customerId)?.eposta
                                  : formData.customerEmail) || "-"}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Typography variant="body2" color="text.secondary">
                                <strong>Telefon:</strong>{" "}
                                {(canSelectCustomer
                                  ? customers.find((c) => c.id === formData.customerId)?.telefon
                                  : formData.customerPhone) || "-"}
                              </Typography>
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </MDBox>
                  </Grid>
                </>
              )}
            </Grid>
          </MDBox>
        );

      case 1:
        return (
          <MDBox>
            <MDTypography variant="h6" mb={2}>
              Talep Detayları
            </MDTypography>
            <Grid container spacing={3}>
              {/* Malzeme & Tuz Section */}
              <Grid item xs={12}>
                <MDTypography variant="subtitle1" fontWeight="bold" mb={2}>
                  Malzeme & Tuz
                </MDTypography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  freeSolo={false}
                  options={materialTypes}
                  value={formData.materialType || null}
                  onChange={(e, newValue) => handleChange("materialType", newValue || "")}
                  renderInput={(params) => (
                    <TextField {...params} label="Materyal Tipi *" variant="outlined" />
                  )}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: "56px",
                      display: "flex",
                      alignItems: "center",
                      paddingRight: "9px !important",
                    },
                    "& .MuiAutocomplete-endAdornment": {
                      top: "50%",
                      transform: "translateY(-50%)",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  freeSolo={false}
                  options={saltTypes}
                  value={formData.saltSize || null}
                  onChange={(e, newValue) => handleChange("saltSize", newValue || "")}
                  renderInput={(params) => (
                    <TextField {...params} label="Tuz Boyutu *" variant="outlined" />
                  )}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: "56px",
                      display: "flex",
                      alignItems: "center",
                      paddingRight: "9px !important",
                    },
                    "& .MuiAutocomplete-endAdornment": {
                      top: "50%",
                      transform: "translateY(-50%)",
                    },
                  }}
                />
              </Grid>

              {/* Ödeme Section */}
              <Grid item xs={12}>
                <MDTypography variant="subtitle1" fontWeight="bold" mb={2} mt={2}>
                  Ödeme
                </MDTypography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  freeSolo={false}
                  options={paymentTypes}
                  value={formData.paymentType || null}
                  onChange={(e, newValue) => handleChange("paymentType", newValue || "")}
                  renderInput={(params) => (
                    <TextField {...params} label="Ödeme Tipi *" variant="outlined" />
                  )}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: "56px",
                      display: "flex",
                      alignItems: "center",
                      paddingRight: "9px !important",
                    },
                    "& .MuiAutocomplete-endAdornment": {
                      top: "50%",
                      transform: "translateY(-50%)",
                    },
                  }}
                />
              </Grid>
              {formData.paymentType === "Taksit" && (
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Taksit Sayısı (Vade Gün)"
                    type="number"
                    value={formData.term}
                    onChange={(e) => handleChange("term", e.target.value)}
                    fullWidth
                    variant="outlined"
                    size="medium"
                    sx={textFieldSx}
                    InputProps={{ inputProps: { min: 0, inputMode: "numeric", pattern: "[0-9]*" } }}
                  />
                </Grid>
              )}

              {/* Sınıflandırma Section */}
              <Grid item xs={12}>
                <MDTypography variant="subtitle1" fontWeight="bold" mb={2} mt={2}>
                  Sınıflandırma
                </MDTypography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  freeSolo={false}
                  options={classifications}
                  value={formData.classification || null}
                  onChange={(e, newValue) => handleChange("classification", newValue || "")}
                  renderInput={(params) => (
                    <TextField {...params} label="Sınıflandırma *" variant="outlined" />
                  )}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: "56px",
                      display: "flex",
                      alignItems: "center",
                      paddingRight: "9px !important",
                    },
                    "& .MuiAutocomplete-endAdornment": {
                      top: "50%",
                      transform: "translateY(-50%)",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  freeSolo={false}
                  options={packagings}
                  value={formData.packaging || null}
                  onChange={(e, newValue) => handleChange("packaging", newValue || "")}
                  renderInput={(params) => (
                    <TextField {...params} label="Paketleme *" variant="outlined" />
                  )}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: "56px",
                      display: "flex",
                      alignItems: "center",
                      paddingRight: "9px !important",
                    },
                    "& .MuiAutocomplete-endAdornment": {
                      top: "50%",
                      transform: "translateY(-50%)",
                    },
                  }}
                />
              </Grid>

              {/* Ek Bilgiler Section */}
              <Grid item xs={12}>
                <MDTypography variant="subtitle1" fontWeight="bold" mb={2} mt={2}>
                  Ek Bilgiler
                </MDTypography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Yorum"
                  value={formData.comment}
                  onChange={(e) => handleChange("comment", e.target.value)}
                  fullWidth
                  variant="outlined"
                  multiline
                  rows={2}
                  inputProps={{ maxLength: 90 }}
                  helperText={`${90 - (formData.comment?.length || 0)} karakter kaldı`}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      padding: "12px 14px",
                    },
                    "& .MuiOutlinedInput-input": {
                      padding: "8px 0",
                    },
                  }}
                />
              </Grid>
            </Grid>
          </MDBox>
        );

      case 2:
        return (
          <MDBox>
            <MDTypography variant="h6" mb={3}>
              Miktar & Tarih
            </MDTypography>
            <Grid container spacing={3}>
              {/* Fiyat Bilgileri Section - Only for Manager and Salesman */}
              {canSelectCustomer && (
                <>
                  <Grid item xs={12}>
                    <MDTypography variant="subtitle1" fontWeight="bold" mb={2}>
                      Fiyat Bilgileri
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ color: "#999", fontWeight: 500, mb: 1, display: "block" }}
                      >
                        Toplam Fiyat
                      </Typography>
                      <TextField
                        placeholder="Örn. 1.000"
                        type="text"
                        value={formData.price}
                        onChange={(e) => handleChange("price", e.target.value)}
                        fullWidth
                        variant="outlined"
                        size="medium"
                        sx={textFieldSx}
                        InputProps={{ inputProps: { inputMode: "numeric", pattern: "[0-9.]*" } }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ color: "#999", fontWeight: 500, mb: 1, display: "block" }}
                      >
                        Ton Başı Fiyat
                      </Typography>
                      <TextField
                        placeholder="Örn. 1.000"
                        type="text"
                        value={formData.pricePerTon}
                        onChange={(e) => handleChange("pricePerTon", e.target.value)}
                        fullWidth
                        variant="outlined"
                        size="medium"
                        sx={textFieldSx}
                        InputProps={{ inputProps: { inputMode: "numeric", pattern: "[0-9.]*" } }}
                      />
                    </Box>
                  </Grid>
                </>
              )}

              {/* Miktar Section */}
              <Grid item xs={12}>
                <MDTypography variant="subtitle1" fontWeight="bold" mb={2} mt={1}>
                  Miktar
                </MDTypography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "#999", fontWeight: 500, mb: 1, display: "block" }}
                  >
                    Miktar *
                  </Typography>
                  <TextField
                    placeholder="Örn. 1.000"
                    type="text"
                    value={formData.amount}
                    onChange={(e) => handleChange("amount", e.target.value)}
                    fullWidth
                    variant="outlined"
                    size="medium"
                    sx={textFieldSx}
                    InputProps={{ inputProps: { inputMode: "numeric", pattern: "[0-9.]*" } }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "#999", fontWeight: 500, mb: 1, display: "block" }}
                  >
                    Tip *
                  </Typography>
                  <Autocomplete
                    freeSolo={false}
                    options={["Ton", "Tır", "Kilogram"]}
                    value={formData.quantityType || null}
                    onChange={(e, newValue) => {
                      handleChange("quantityType", newValue || "");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="tir / ton / kilogram"
                        variant="outlined"
                      />
                    )}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "56px",
                        display: "flex",
                        alignItems: "center",
                        paddingRight: "9px !important",
                      },
                      "& .MuiAutocomplete-endAdornment": {
                        top: "50%",
                        transform: "translateY(-50%)",
                      },
                    }}
                  />
                </Box>
              </Grid>

              {/* Teslim Tarihi Section */}
              <Grid item xs={12}>
                <MDTypography variant="subtitle1" fontWeight="bold" mb={2} mt={1}>
                  Teslim Tarihi
                </MDTypography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  type="date"
                  value={
                    formData.deliveryDate
                      ? formData.deliveryDate instanceof Date
                        ? formData.deliveryDate.toISOString().split("T")[0]
                        : formData.deliveryDate
                      : ""
                  }
                  onChange={(e) => handleChange("deliveryDate", e.target.value)}
                  placeholder="Teslim Tarihi seçilmedi"
                  fullWidth
                  variant="outlined"
                  size="medium"
                  sx={textFieldSx}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    min: new Date().toISOString().split("T")[0],
                  }}
                />
              </Grid>

              {/* Fabrika Seçimi Section */}
              <Grid item xs={12}>
                <MDTypography variant="subtitle1" fontWeight="bold" mb={2} mt={2}>
                  Fabrika Seçimi
                </MDTypography>
              </Grid>
              <Grid item xs={12}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "#999", fontWeight: 500, mb: 1, display: "block" }}
                  >
                    Fabrika Seç
                  </Typography>
                  <Autocomplete
                    freeSolo={false}
                    options={factories}
                    getOptionLabel={(option) => option.fabrikaAdi || ""}
                    value={selectedFactory}
                    onChange={(e, newValue) => {
                      if (newValue) {
                        setSelectedFactory(newValue);
                        setFormData((prev) => ({
                          ...prev,
                          selectedFactoryId: newValue.id,
                          fabrikaAdi: newValue.fabrikaAdi || "",
                          fabrikaSorumlusu: newValue.fabrikaSorumlusu || "",
                          fabrikaTelefon: newValue.fabrikaTelefon || "",
                          fabrikaAdres: newValue.adres || "",
                        }));
                      } else {
                        setSelectedFactory(null);
                        setFormData((prev) => ({
                          ...prev,
                          selectedFactoryId: "",
                          fabrikaAdi: "",
                          fabrikaSorumlusu: "",
                          fabrikaTelefon: "",
                          fabrikaAdres: "",
                        }));
                      }
                    }}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Fabrika seçin" variant="outlined" />
                    )}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "56px",
                        display: "flex",
                        alignItems: "center",
                        paddingRight: "9px !important",
                      },
                      "& .MuiAutocomplete-endAdornment": {
                        top: "50%",
                        transform: "translateY(-50%)",
                      },
                    }}
                  />
                </Box>
              </Grid>
              {formData.fabrikaAdi && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      mt: 2,
                      p: 2.5,
                      bgcolor: "#f5f5f5",
                      borderRadius: 1,
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      mb={2}
                      sx={{ borderBottom: "2px solid #ddd", pb: 1 }}
                    >
                      Seçilen Fabrika: {formData.fabrikaAdi}
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      <Box>
                        <Typography variant="caption" sx={{ color: "#999", fontWeight: 600 }}>
                          Sorumlu
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#333", fontWeight: 500 }}>
                          {formData.fabrikaSorumlusu || "-"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: "#999", fontWeight: 600 }}>
                          Telefon
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#333", fontWeight: 500 }}>
                          {formData.fabrikaTelefon || "-"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: "#999", fontWeight: 600 }}>
                          Adres
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#333", fontWeight: 500 }}>
                          {formData.fabrikaAdres || "-"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          </MDBox>
        );

      default:
        return null;
    }
  };

  if (userLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox p={3}>
          <Typography>Yükleniyor...</Typography>
        </MDBox>
      </DashboardLayout>
    );
  }

  if (!hasAccess) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox p={3}>
          <Typography>Bu sayfaya erişim yetkiniz yok.</Typography>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container justifyContent="center">
          <Grid item xs={12} lg={10}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h4" fontWeight="medium" mb={3}>
                  Yeni Sipariş Oluştur
                </MDTypography>
                <Stepper
                  activeStep={activeStep}
                  alternativeLabel
                  sx={{
                    "& .MuiStepIcon-root": {
                      color: ({ palette }) => palette.grey[300],
                      "&.Mui-active": {
                        color: ({ palette }) => palette.info.main,
                      },
                      "&.Mui-completed": {
                        color: ({ palette }) => palette.info.main,
                      },
                    },
                    "& .MuiStepConnector-line": {
                      borderColor: ({ palette }) => palette.grey[300],
                    },
                    "& .MuiStepConnector-root.Mui-active .MuiStepConnector-line, & .MuiStepConnector-root.Mui-completed .MuiStepConnector-line":
                      {
                        borderColor: ({ palette }) => palette.info.main,
                      },
                    "& .MuiStepLabel-label": {
                      color: ({ palette }) => palette.text.disabled,
                      "&.Mui-active": {
                        color: ({ palette }) => palette.info.main,
                        fontWeight: 600,
                      },
                      "&.Mui-completed": {
                        color: ({ palette }) => palette.info.main,
                        fontWeight: 600,
                      },
                    },
                  }}
                >
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
                <MDBox mt={4} mb={2}>
                  {renderStepContent(activeStep)}
                </MDBox>
                <MDBox display="flex" justifyContent="space-between" mt={4}>
                  <MDButton
                    variant="outlined"
                    color="secondary"
                    onClick={handleBack}
                    disabled={activeStep === 0}
                  >
                    Geri
                  </MDButton>
                  {activeStep === steps.length - 1 ? (
                    <MDButton variant="gradient" color="success" onClick={handleSubmit}>
                      Siparişi Oluştur
                    </MDButton>
                  ) : (
                    <MDButton variant="gradient" color="info" onClick={handleNext}>
                      İleri
                    </MDButton>
                  )}
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default CreateOrder;
